/* eslint-disable @typescript-eslint/indent */
import React, { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { styles, CheckIndicator, CheckIndicatorStatus } from '@sdir/sds';
import {
  GetApplicationsQueryResult,
  SimulatorResultBaseItem,
  SimulatorResultItem,
} from '@sdir/httpclient/lib/clients/aps/application';
import { useIntl } from 'react-intl';
import { QualificationActions } from '../QualificationActions';
import { AddedQualifications, QualificationType } from '../../../types';
import {
  errorTranslationForQualification,
  qualificationExists,
  qualificationIsApproved,
  qualificationIsComplete,
  qualificationIsRejected,
} from '../../../helpers/Qualifications';
import { Card, InputError } from '../../Atoms';
import {
  determineQualificationCardStatus,
  QualificationCardStatus,
} from '../QualificationBaseCard/QualificationBaseCard';
import { useCurrentUserInfo } from '../../../services/hooks/useCurrentUserInfo';

interface QualificationAlternativeCardProps {
  number: number;
  application: GetApplicationsQueryResult;
  qualifications: SimulatorResultBaseItem;
  addedQualifications: AddedQualifications;
  showValidation?: boolean;
  isNewApplication?: boolean;
  cardStatus?: QualificationCardStatus;
  enableNewSeagoing?: boolean;
}

export const determineAlternativeStatus = (
  isNewApplication,
  qualifications,
  addedQualifications,
  attachments,
  confirmations
) => {
  const hasDeclaration = qualifications?.items?.some(
    (q) => q.type === QualificationType.Declaration
  );
  if (qualifications?.status && !hasDeclaration) return QualificationCardStatus.Valid;
  const hasAnyAddedInfo = !!qualifications.items?.find((q) =>
    qualificationExists(q, addedQualifications, attachments)
  );

  // If incomplete -> the applicant has not provided all info when applying
  const isIncomplete = !!qualifications.items?.find(
    (q) => !qualificationIsComplete(q, addedQualifications, attachments, confirmations)
  );

  let allValid = true;
  qualifications?.items?.forEach((q) => {
    if (!qualificationIsApproved(q, addedQualifications, attachments, confirmations)) {
      if (isNewApplication) {
        allValid = false;
      } else {
        const isRejected = qualificationIsRejected(q, addedQualifications, attachments);
        if (isIncomplete || isRejected) {
          allValid = false;
        }
      }
    }
  });

  const isComplete = isNewApplication ? !isIncomplete : !!qualifications?.status || allValid;
  if (isComplete) return QualificationCardStatus.Valid;
  if (isIncomplete) return QualificationCardStatus.Warning;
  return QualificationCardStatus.Warning;
};

export const QualificationAlternativeCard: FC<QualificationAlternativeCardProps> = ({
  number,
  application,
  addedQualifications,
  qualifications,
  showValidation = false,
  isNewApplication = false,
  cardStatus = QualificationCardStatus.None,
  enableNewSeagoing = false,
}) => {
  const intl = useIntl();
  const {
    userInfo: { isExternal },
  } = useCurrentUserInfo();

  const { sortedItems, lastSeagoingUri, seagoingStatus } = useMemo(() => {
    if (!qualifications?.items) {
      return { sortedItems: [], lastSeagoingUri: null, seagoingStatus: null };
    }
    const items: any =
      qualifications?.items?.sort((a, b) => {
        if (!a.title || !b.title) return 0;
        if (a.title < b.title) return -1;
        if (a.title > b.title) return 1;
        return 0;
      }) || [];
    const reverseItems = [...items].reverse();
    const lastSeagoing = reverseItems?.find(
      (s) =>
        s.type === QualificationType.SeagoingTime &&
        !qualificationIsApproved(
          s,
          addedQualifications,
          application.attachments || [],
          application.confirmedQualifications || []
        )
    );
    const seagoingIncomplete = items?.some((i) => {
      const status = determineQualificationCardStatus(
        isNewApplication,
        i,
        addedQualifications,
        application.attachments || [],
        application?.confirmedQualifications || []
      );
      return (
        i.type === QualificationType.SeagoingTime && status === QualificationCardStatus.Warning
      );
    });
    return {
      sortedItems: items,
      lastSeagoingUri: lastSeagoing?.uri,
      seagoingStatus: seagoingIncomplete ? QualificationCardStatus.Warning : null,
    };
  }, [qualifications?.items, addedQualifications, isNewApplication, application?.attachments]);

  return (
    <AlternativeCard className={`status--${cardStatus}`}>
      <div data-testid={`alt-card-${number}`}>
        <h3>{intl.formatMessage({ id: 'additionalqualifications.alternative' }, { number })}</h3>
        {sortedItems?.map((q, i) => {
          const status = determineQualificationCardStatus(
            isNewApplication,
            q,
            addedQualifications,
            application?.attachments || [],
            application?.confirmedQualifications || []
          );
          const isSeagoing = q.type === QualificationType.SeagoingTime;
          const hideActions = isSeagoing && lastSeagoingUri !== q.uri;
          const actionsStatus = isSeagoing && !isExternal ? seagoingStatus || undefined : undefined;
          return (
            <QualificationItem
              key={i}
              alternativeIndex={number - 1}
              application={application}
              qualification={q}
              externalStatus={status as CheckIndicatorStatus}
              actionsStatus={actionsStatus as CheckIndicatorStatus}
              inverted={false}
              showSeparator={!hideActions && i !== sortedItems.length - 1}
              hideActions={enableNewSeagoing && !isExternal ? hideActions : false}
              isNewApplication={isNewApplication}
              addedQualifications={addedQualifications}
              showValidation={showValidation}
              isExternalUser={isExternal}
            />
          );
        })}
      </div>
    </AlternativeCard>
  );
};

const AlternativeCard = styled(Card)`
  padding: 20px 20px;

  h3 {
    text-align: center;
    font-size: 24px;
    margin: 0 0 10px 0;
    font-family: ${styles.fonts.stfMedium};
  }

  .error-state-item + .error-state-item {
    margin-top: 24px;
  }

  &.status {
    &--error {
      border: 1px solid ${({ theme }) => theme.colors.alert.errorDark};
      background-color: ${({ theme }) => theme.colors.alert.errorLight};
    }
    &--warning {
      border: 1px solid ${({ theme }) => theme.colors.alert.warningDark};
      background-color: ${({ theme }) => theme.colors.alert.warningLight};
    }
    &--valid {
      border: 1px solid ${({ theme }) => theme.colors.alert.successDark};
      background-color: ${({ theme }) => theme.colors.alert.successLight};
    }
  }
`;

const QualificationItem: FC<{
  qualification: SimulatorResultItem;
  application: GetApplicationsQueryResult;
  inverted: boolean;
  addedQualifications: AddedQualifications;
  showValidation: boolean;
  isNewApplication: boolean;
  hideActions: boolean;
  showSeparator: boolean;
  externalStatus?: CheckIndicatorStatus;
  actionsStatus?: CheckIndicatorStatus;
  alternativeIndex: number;
  isExternalUser?: boolean;
}> = ({
  qualification,
  application,
  inverted,
  addedQualifications,
  showValidation,
  isNewApplication,
  hideActions = false,
  showSeparator = false,
  externalStatus,
  actionsStatus,
  alternativeIndex,
  isExternalUser = false,
}) => {
  const intl = useIntl();

  const isComplete = useMemo(() => {
    if (qualification.type === QualificationType.Declaration) {
      return false;
    }
    if (isNewApplication) {
      return qualificationIsComplete(
        qualification,
        addedQualifications,
        application.attachments || [],
        application.confirmedQualifications || []
      );
    }
    return qualificationIsApproved(
      qualification,
      addedQualifications,
      application.attachments || [],
      application.confirmedQualifications || []
    );
  }, [addedQualifications, application?.attachments, qualification, isNewApplication]);

  const isApproved = useMemo(() => {
    if (qualification.type === QualificationType.Declaration) {
      return false;
    }
    return qualificationIsApproved(
      qualification,
      addedQualifications,
      application.attachments || [],
      application.confirmedQualifications || []
    );
  }, [addedQualifications, application?.attachments, qualification]);

  const indicatorStatus = useMemo(() => {
    if (externalStatus) return externalStatus;
    if (isApproved) return 'valid';
    return isComplete ? 'valid' : showValidation ? 'error' : 'warning';
  }, [isApproved, isComplete, showValidation, externalStatus]);

  const errorState = !isComplete && showValidation && indicatorStatus === 'error';
  const classes = `${errorState ? 'error-state-item' : ''} ${showSeparator ? 'separator' : ''}`;

  return (
    <QualificationItemContainer inverted={inverted} error={errorState} className={classes}>
      <div className="qualification-contents">
        <div>
          <div>
            <CheckIndicator size="m" status={indicatorStatus} inverted={inverted && !errorState} />
          </div>
          <div>
            <p className="qualification-text">
              <strong>{`${qualification.title}:`}</strong>
              <div dangerouslySetInnerHTML={{ __html: qualification.description || '' }} />
            </p>
          </div>
        </div>
        {!isApproved && !hideActions && (
          <QualificationActions
            qualification={qualification}
            addedQualifications={addedQualifications}
            qualificationStatus={actionsStatus || indicatorStatus}
            qualificationKey={`alternatives-${alternativeIndex + 1}`}
            isExternalUser={isExternalUser}
          />
        )}
      </div>
      {errorState && (
        <QualificationError>
          {intl.formatMessage({ id: errorTranslationForQualification(qualification) })}
        </QualificationError>
      )}
    </QualificationItemContainer>
  );
};

const QualificationItemContainer = styled.div<{ inverted: boolean; error: boolean }>`
  .qualification-contents {
    position: relative;
    display: flex;
    flex-direction: column;

    ${({ error, theme }) =>
      error
        ? css`
            margin-top: -2px;
            background: ${theme.colors.alert.errorLight};
            border-top: 2px solid ${theme.colors.alert.errorDark};
            border-bottom: 2px solid ${theme.colors.alert.errorDark};
            padding: 2px 0;
          `
        : css``}

    & > div:first-child {
      display: flex;
      & > :first-child {
        flex: 0 0 auto;
        margin-right: 16px;
      }
      & > :last-child {
        flex: 1 1 auto;

        p {
          margin: 0;
        }
      }
    }
  }

  .qualification-text {
    line-height: 1.5;

    & > div {
      display: inline;
      margin-left: 5px;
    }
  }

  &.separator:not(.error-state-item) {
    border-bottom: 1px solid #000000;
    padding-bottom: 15px;
    margin-bottom: 15px;
  }

  &:not(.separator) {
    margin-bottom: 5px;
  }
`;

const QualificationError = styled(InputError)`
  width: calc(100% + 8px);
  padding: 0 32px;
  width: 382px;
  top: 100%;
  left: 0;
`;
